import type { IDBPDatabase } from 'idb';
import { STORAGE_SCHEMA_VERSION } from '@texas42/app-model';
import {
  ALL_STORES,
  DB_NAME,
  STORE_ADAPTIVE_PROFILES,
  STORE_COMPLETED_HANDS,
  STORE_MATCHES,
  STORE_META,
  STORE_PREFERENCES,
  STORE_RULESETS,
  type Texas42StoreName,
} from './constants.js';

export type Texas42Database = IDBPDatabase<Texas42Schema>;

export interface Texas42Schema {
  [STORE_MATCHES]: {
    key: string;
    value: unknown;
    indexes: {
      updatedAt: string;
      status: string;
      difficulty: string;
    };
  };
  [STORE_COMPLETED_HANDS]: {
    key: string;
    value: unknown;
    indexes: {
      matchId: string;
      completedAt: string;
      difficulty: string;
    };
  };
  [STORE_RULESETS]: {
    key: string;
    value: unknown;
    indexes: {
      updatedAt: string;
    };
  };
  [STORE_PREFERENCES]: {
    key: string;
    value: unknown;
  };
  [STORE_ADAPTIVE_PROFILES]: {
    key: string;
    value: unknown;
  };
  [STORE_META]: {
    key: string;
    value: unknown;
  };
}

function createV1Stores(db: IDBPDatabase<Texas42Schema>, _oldVersion: number): void {
  if (!db.objectStoreNames.contains(STORE_MATCHES)) {
    const matches = db.createObjectStore(STORE_MATCHES, { keyPath: 'payload.id' });
    matches.createIndex('updatedAt', 'payload.updatedAt');
    matches.createIndex('status', 'payload.status');
    matches.createIndex('difficulty', 'payload.difficulty');
  }

  if (!db.objectStoreNames.contains(STORE_COMPLETED_HANDS)) {
    const hands = db.createObjectStore(STORE_COMPLETED_HANDS, { keyPath: 'payload.id' });
    hands.createIndex('matchId', 'payload.matchId');
    hands.createIndex('completedAt', 'payload.completedAt');
    hands.createIndex('difficulty', 'payload.difficulty');
  }

  if (!db.objectStoreNames.contains(STORE_RULESETS)) {
    const rulesets = db.createObjectStore(STORE_RULESETS, { keyPath: 'payload.id' });
    rulesets.createIndex('updatedAt', 'payload.updatedAt');
  }

  if (!db.objectStoreNames.contains(STORE_PREFERENCES)) {
    db.createObjectStore(STORE_PREFERENCES, { keyPath: 'payload.key' });
  }

  if (!db.objectStoreNames.contains(STORE_ADAPTIVE_PROFILES)) {
    db.createObjectStore(STORE_ADAPTIVE_PROFILES, { keyPath: 'payload.profileId' });
  }

  if (!db.objectStoreNames.contains(STORE_META)) {
    db.createObjectStore(STORE_META, { keyPath: 'key' });
  }
}

const MIGRATIONS: Record<number, (db: IDBPDatabase<Texas42Schema>, oldVersion: number) => void> = {
  1: createV1Stores,
  /** Idempotent repair for legacy v1 databases missing later stores. */
  2: createV1Stores,
};

export async function runMigrations(
  db: IDBPDatabase<Texas42Schema>,
  oldVersion: number,
): Promise<void> {
  for (let version = oldVersion + 1; version <= STORAGE_SCHEMA_VERSION; version += 1) {
    const migrate = MIGRATIONS[version];
    if (!migrate) {
      throw new Error(`Missing migration for schema version ${version}`);
    }
    migrate(db, oldVersion);
  }
}

export async function openTexas42Database(
  idbFactory: typeof globalThis.indexedDB = globalThis.indexedDB,
): Promise<Texas42Database> {
  if (!idbFactory) {
    throw new Error('IndexedDB is not available in this environment');
  }

  const { openDB } = await import('idb');

  return openDB<Texas42Schema>(DB_NAME, STORAGE_SCHEMA_VERSION, {
    upgrade(db, _oldVersion, _newVersion, transaction) {
      void transaction;
      const fromVersion = _oldVersion;
      for (let version = fromVersion + 1; version <= STORAGE_SCHEMA_VERSION; version += 1) {
        const migrate = MIGRATIONS[version];
        if (!migrate) {
          throw new Error(`Missing migration for schema version ${version}`);
        }
        migrate(db, fromVersion);
      }
    },
    blocked() {
      throw new StorageBlockedError();
    },
  });
}

export class StorageBlockedError extends Error {
  constructor() {
    super('Another tab has the save database open. Close other Texas 42 tabs and reload.');
    this.name = 'StorageBlockedError';
  }
}

export function isTexas42StoreName(name: string): name is Texas42StoreName {
  return (ALL_STORES as readonly string[]).includes(name);
}
